import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './PriorizacaoDashboard.css';
import { FaExclamationTriangle, FaChartLine, FaCheckDouble, FaListOl, FaClipboardList } from 'react-icons/fa';
import { getClassificacaoIAP } from '../../utils/iap';

const PriorizacaoDashboard = ({ questoes, priorizacoes, respostas, calculateIAP, customEmptyMessage }) => {
    const navigate = useNavigate();

    const metrics = useMemo(() => {
        let totalPriorizadas = 0;
        let totalEssenciais = 0;
        let totalComGapCritico = 0;

        const criticalList = [];

        questoes.forEach(q => {
            const priorizacao = priorizacoes[q.id];
            const iap = priorizacao ? calculateIAP(priorizacao.missao, priorizacao.seguranca, priorizacao.viabilidade) : null;

            // Só consideramos se tiver IAP calculado
            if (iap !== null && iap > 0) {
                totalPriorizadas++;
                if (iap >= 4.1) totalEssenciais++;

                // Dados de Maturidade
                const resposta = respostas[q.id];
                const pontuacao = resposta?.pontuacao || 0; // 0 a 1
                const maturidadePercent = pontuacao * 100;

                // IAP normalizado para 0-100 (5.0 = 100%)
                const iapPercent = (iap / 5) * 100;

                // Gap: Importância - Maturidade
                // Se Importância é alta (100) e Maturidade é baixa (0), Gap = 100 (Crítico)
                // Se Importância é baixa (20) e Maturidade é alta (100), Gap = -80 (Não crítico)
                let gap = iapPercent - maturidadePercent;

                // Só consideramos gaps positivos (onde a importância supera a maturidade)
                if (gap < 0) gap = 0;

                // Definindo critério de "Super Crítico": Gap > 50 (Ex: Essencial 100% - Maturidade < 50%)
                // O usuário disse: "Essencial e inexpressivo" -> 100 - 0 = 100.
                if (gap >= 50) {
                    totalComGapCritico++;
                }

                criticalList.push({
                    id: q.id,
                    codigo: q.codigo,
                    enunciado: q.enunciado,
                    iap: iap,
                    maturidade: maturidadePercent,
                    gap: gap,
                    classificacao: getClassificacaoIAP(iap)
                });
            }
        });

        // Ordenar por maior Gap (Críticos no topo)
        criticalList.sort((a, b) => b.gap - a.gap);

        // Pegar Top 10
        const top10 = criticalList.slice(0, 10);

        return {
            totalPriorizadas,
            totalEssenciais,
            totalComGapCritico,
            top10,
            percentualPriorizado: questoes.length > 0 ? (totalPriorizadas / questoes.length) * 100 : 0
        };

    }, [questoes, priorizacoes, respostas, calculateIAP]);

    const getMaturidadeLabel = (perc) => {
        if (perc >= 70) return { label: 'Aprimorado', class: 'aprimorado' };
        if (perc >= 40) return { label: 'Intermediário', class: 'intermediario' };
        if (perc >= 15) return { label: 'Inicial', class: 'inicial' };
        return { label: 'Inexpressivo', class: 'inexpressivo' };
    };

    if (metrics.totalPriorizadas === 0) {
        return (
            <div className="priorizacao-dashboard empty-state-wrapper">
                <div className="dashboard-header">
                    <h2>Indicadores de Priorização & Criticidade</h2>
                    <p>Análise cruzada entre a importância definida (IAP) e a maturidade atual das questões.</p>
                </div>
                <div className="empty-dashboard-state">
                    <FaClipboardList className="empty-icon" />
                    <h3>Nenhuma Priorização Definida</h3>
                    {customEmptyMessage ? (
                        <p>{customEmptyMessage}</p>
                    ) : (
                        <>
                            <p>
                                Ainda não há dados suficientes para gerar os indicadores.
                                <br />
                                Para gerar os indicadores, é necessário preencher a Matriz de Priorização.
                            </p>
                            <button
                                className="btn-primary"
                                style={{ marginTop: '1rem' }}
                                onClick={() => navigate('/adm-org/priorizacao')}
                            >
                                Ir para Matriz de Priorização
                            </button>
                        </>
                    )}
                </div>
            </div >
        );
    }

    return (
        <div className="priorizacao-dashboard">
            <div className="dashboard-header">
                <h2>Indicadores de Priorização & Criticidade</h2>
                <p>Análise cruzada entre a importância definida (IAP) e a maturidade atual das questões.</p>
            </div>

            <div className="kpi-grid">
                <div className="kpi-card blue">
                    <span className="kpi-value">{metrics.totalPriorizadas} <span style={{ fontSize: '1rem', color: '#999' }}>/ {questoes.length}</span></span>
                    <span className="kpi-label"><FaCheckDouble /> Questões Priorizadas</span>
                </div>
                <div className="kpi-card orange">
                    <span className="kpi-value">{metrics.totalEssenciais}</span>
                    <span className="kpi-label"><FaChartLine /> Classificadas como Essenciais</span>
                </div>
                <div className="kpi-card red">
                    <span className="kpi-value">{metrics.totalComGapCritico}</span>
                    <span className="kpi-label"><FaExclamationTriangle /> Gaps Críticos (Alta Importância, Baixa Maturidade)</span>
                </div>
            </div>

            <div className="top-critical-section">
                <h3><FaListOl /> Top 10 - Pontos de Atenção (Maior Gap de Execução)</h3>
                <div className="critical-table-wrapper">
                    <table className="critical-table">
                        <thead>
                            <tr>
                                <th style={{ width: '80px' }}>Cód.</th>
                                <th>Questão</th>
                                <th style={{ width: '120px' }}>Prioridade</th>
                                <th style={{ width: '150px' }}>Maturidade Atual</th>
                                <th style={{ width: '120px' }}>Déficit (Gap)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {metrics.top10.map(item => {
                                const matInfo = getMaturidadeLabel(item.maturidade);
                                return (
                                    <tr key={item.id}>
                                        <td><strong>{item.codigo}</strong></td>
                                        <td>{item.enunciado}</td>
                                        <td>
                                            <span className={`priority-tag ${item.classificacao.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-")}`}>
                                                {item.classificacao} ({(item.iap).toFixed(1)})
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge-nivel ${matInfo.class}`}>
                                                {matInfo.label} ({Math.round(item.maturidade)}%)
                                            </span>
                                        </td>
                                        <td>
                                            <div className="score-value gap-indicator">
                                                -{Math.round(item.gap)} pts
                                            </div>
                                            <div className="score-bar-container">
                                                <div
                                                    className="score-bar-fill"
                                                    style={{
                                                        width: `${Math.min(item.gap, 100)}%`,
                                                        backgroundColor: item.gap > 50 ? '#e74c3c' : '#f39c12'
                                                    }}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {metrics.top10.length === 0 && (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', color: '#999' }}>
                                        Nenhuma questão priorizada encontrada com gap de execução.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PriorizacaoDashboard;
